import React, { forwardRef, useState } from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'
import dayjs from 'dayjs'
import ClickAwayComponent from 'components/ClickAwayComponent'

const PRESETS = ['today', 'last_7_days', 'last_30_days', 'this_month', 'last_month']

export default withTranslation()(function ReportsPeriodFilter({ t, preset, from, to, onSelectPreset, onApplyCustom, onClear }) {
  const [draftFrom, setDraftFrom] = useState(from ?? '')
  const [draftTo, setDraftTo] = useState(to ?? '')

  const isCustom = preset === 'custom' && from && to
  const activeLabel = isCustom
    ? `${dayjs(from).format('MMM D')} - ${dayjs(to).format('MMM D, YYYY')}`
    : (preset && preset !== 'all_time' ? t(`reports.filters.${preset}`) : null)

  return (
    <ClickAwayComponent
      Component={forwardRef(({ handleClick, open }, ref) => (
        <div className="relative" ref={ref}>
          <button
            type="button"
            onClick={handleClick}
            className="flex items-center gap-2 bg-background rounded-full h-9 px-4 text-sm font-medium text-dark-blue whitespace-nowrap"
          >
            <UIcons.UilCalendarAlt size={16} className="text-secondary" />
            {activeLabel ?? t('reports.filters.all_time')}

            {
              activeLabel
                ? (
                  <UIcons.UilTimes
                    size={14}
                    className="text-secondary"
                    onClick={(event) => { event.stopPropagation(); setDraftFrom(''); setDraftTo(''); onClear() }}
                  />
                )
                : (open ? <UIcons.UilAngleUp size={16} className="text-secondary" /> : <UIcons.UilAngleDown size={16} className="text-secondary" />)
            }
          </button>

          {
            open && (
              <div className="absolute right-0 top-11 z-20 w-72 bg-white border border-border-lines rounded-xl shadow-lg p-3">
                <ul className="flex flex-col mb-3">
                  <li>
                    <button
                      type="button"
                      onClick={() => { onSelectPreset('all_time'); handleClick() }}
                      className={`w-full flex items-center justify-between text-left px-2 py-2 text-sm rounded-lg hover:bg-background ${(!preset || preset === 'all_time') ? 'text-primary font-medium' : 'text-dark-blue'}`}
                    >
                      {t('reports.filters.all_time')}
                      {(!preset || preset === 'all_time') && <UIcons.UilCheck size={16} />}
                    </button>
                  </li>

                  {
                    PRESETS.map((key) => (
                      <li key={key}>
                        <button
                          type="button"
                          onClick={() => { onSelectPreset(key); handleClick() }}
                          className={`w-full flex items-center justify-between text-left px-2 py-2 text-sm rounded-lg hover:bg-background ${preset === key ? 'text-primary font-medium' : 'text-dark-blue'}`}
                        >
                          {t(`reports.filters.${key}`)}
                          {preset === key && <UIcons.UilCheck size={16} />}
                        </button>
                      </li>
                    ))
                  }
                </ul>

                <div className="border-t border-border-lines pt-3">
                  <p className="text-xs font-medium text-secondary mb-2">{t('reports.filters.custom_range')}</p>

                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="date"
                      value={draftFrom}
                      onChange={(event) => setDraftFrom(event.target.value)}
                      className="w-full border border-border-lines rounded-lg px-2 py-1.5 text-sm text-dark-blue"
                    />
                    <span className="text-secondary text-sm">-</span>
                    <input
                      type="date"
                      value={draftTo}
                      min={draftFrom || undefined}
                      onChange={(event) => setDraftTo(event.target.value)}
                      className="w-full border border-border-lines rounded-lg px-2 py-1.5 text-sm text-dark-blue"
                    />
                  </div>

                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      disabled={!draftFrom || !draftTo}
                      onClick={() => { onApplyCustom(draftFrom, draftTo); handleClick() }}
                      className="bg-primary text-white text-sm font-medium rounded-full px-4 py-1.5 disabled:opacity-40"
                    >
                      {t('reports.filters.apply')}
                    </button>
                  </div>
                </div>
              </div>
            )
          }
        </div>
      ))}
    />
  )
})
